<script setup>
import { computed } from 'vue';
import {POST_ANIMATION_FADE_IN_DURATION_MS, postAnimationEffectsCompleted} from "@/components/seo-score/visibility-chart/composables/animation";
import {
  VIEWBOX_CENTER,
  ROTATION_OFFSET_DEG,
  SEGMENTS,
  SEGMENT_GAP_DEGREES,
  TOTAL_DEGREE_SPAN,
  RADIUS_RATIO,
  SEGMENT_CONFIG,
  arcPath,
  polarToCartesian,
  industryAverageTooltip,
} from '../composables/radialChart';

const props = defineProps({
  industryAverage: {
    type: [Number, null],
    required: false,
    default: null,
  },
  startPostAnimationEffects: {
    type: Boolean,
    default: false,
  },
});

// Inner Track radius is slightly smaller than the main radius.
const radius = computed(() =>
  Math.max(0, VIEWBOX_CENTER * RADIUS_RATIO - 4),
);

const animationCompleted = postAnimationEffectsCompleted(() => props.startPostAnimationEffects);
const markerTooltip = computed(() => industryAverageTooltip(props.industryAverage, animationCompleted.value));

const industryAverageMarker = computed(() => {
  const value = Math.max(0, Math.min(100, props.industryAverage));

  const usableSweep = TOTAL_DEGREE_SPAN - SEGMENT_GAP_DEGREES * (SEGMENTS - 1);
  const sweepPerSegment = usableSweep / SEGMENTS;
  const baseStart = -TOTAL_DEGREE_SPAN / 2 + ROTATION_OFFSET_DEG;

  // Find which segment this value belongs to, using the same value ranges
  // as the radial segments so angles line up.
  const configIndex = SEGMENT_CONFIG.findIndex(
    (config, index) => {
      const isLast = index === SEGMENT_CONFIG.length - 1;
      if (isLast) {
        return value >= config.min && value <= config.max;
      }

      return value >= config.min && value < config.max;
    },
  );

  if (configIndex === -1) {
    return null;
  }

  const config = SEGMENT_CONFIG[configIndex];
  const startAngle = baseStart + configIndex * (sweepPerSegment + SEGMENT_GAP_DEGREES);

  const rangeSpan = config.max - config.min || 1;
  const rawFraction = (value - config.min) / rangeSpan;
  const fillFraction = Math.min(1, Math.max(0, rawFraction));

  const angle = startAngle + sweepPerSegment * fillFraction;
  const pos = polarToCartesian(angle, radius.value);

  return {
    x: pos.x,
    y: pos.y,
  };
});
</script>

<template>
  <g :transform="`translate(${VIEWBOX_CENTER}, ${VIEWBOX_CENTER})`">
    <path
      :d="
        arcPath(
          -TOTAL_DEGREE_SPAN / 2 + ROTATION_OFFSET_DEG,
          TOTAL_DEGREE_SPAN / 2 + ROTATION_OFFSET_DEG,
          radius,
        )
      "
      class="tw-stroke-neutral-300"
      fill="none"
      :stroke-width="0.5"
      stroke-linecap="round"
    />

    <circle
      v-if="industryAverage > 0"
      v-tooltip="markerTooltip"
      :cx="industryAverageMarker.x"
      :cy="industryAverageMarker.y"
      :r="3 * 0.4"
      :class="['tw-fill-neutral-900', { 'tw-cursor-pointer': markerTooltip }]"
      :style="{
        opacity: startPostAnimationEffects ? 1 : 0,
        transition: `opacity ${POST_ANIMATION_FADE_IN_DURATION_MS}ms ease`,
      }"
    />
  </g>
</template>

